// src/components/Navbar.jsx
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoCircuit from "./LogoCircuit";
import LangSwitch from "./LangSwitch";
import LocalizedNavLink from "./LocalizedNavLink";

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  const pillBase =
    "inline-flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-medium transition-all text-slate-700 hover:text-indigo-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50";
  const pillActive = "text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200";

  const links = [
    { to: "/services", label: t("nav.services") },
    { to: "/projects", label: t("nav.projects") },
    { to: "/applications", label: t("nav.applications") },
    { to: "/process", label: t("nav.process") },
    { to: "/about", label: t("nav.about") },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur">
      <div className="mx-auto max-w-7xl px-3 md:px-4 py-3 flex items-center justify-between min-w-0">
        {/* Brand (click -> localized home) */}
        <LocalizedNavLink to="/" className="flex items-center gap-1 flex-shrink-0">
          {/* smaller on mobile so controls fit */}
          <LogoCircuit className="h-8 w-auto md:h-14" accent="#4F46E5" />
          <span className="sr-only">AndreiTech</span>
        </LocalizedNavLink>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-2">
          {links.map(({ to, label }) => (
            <LocalizedNavLink
              key={to}
              to={to}
              className={({ isActive }) => `${pillBase} ${isActive ? pillActive : ""}`}
            >
              {label}
            </LocalizedNavLink>
          ))}

          <LocalizedNavLink
            to="/contact"
            className="ml-2 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-md hover:brightness-105 transition-all active:translate-y-[1px]"
          >
            {t("nav.quote")}
          </LocalizedNavLink>

          <div className="ml-3">
            <LangSwitch />
          </div>
        </nav>

        {/* Mobile: Lang switch + burger (Lang left of burger) */}
        <div className="md:hidden flex items-center gap-2 flex-shrink-0">
          {/* Slightly compact on mobile */}
          <div className="scale-90 origin-right">
            <LangSwitch />
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((s) => !s)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur">
          <nav className="mx-auto max-w-7xl px-4 py-3 flex flex-col gap-1.5 text-base">
            {links.map(({ to, label }) => (
              <LocalizedNavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `block rounded-xl px-3 py-2 transition-all ${
                    isActive
                      ? "text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200"
                      : "text-slate-700 hover:bg-slate-100 hover:text-indigo-700"
                  }`
                }
              >
                {label}
              </LocalizedNavLink>
            ))}

            <LocalizedNavLink
              to="/contact"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-md hover:brightness-105 transition-all active:translate-y-[1px]"
            >
              {t("nav.quote")}
            </LocalizedNavLink>
          </nav>
        </div>
      )}
    </header>
  );
}
